import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { Input, Spin } from "antd";
import logo from "../../assets/logo.png";
import { getCurrentLanguage, navItems } from "@/store/data/global";
import {
  ArrowRightOutlined,
  CheckOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import "./index.less";

const { TextArea } = Input;

const BottomPage = () => {
  const location = useLocation();
  const currentLanguage = useSelector(getCurrentLanguage);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: ""
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const [formNotice, setFormNotice] = useState(null);
  const noticeTimerRef = useRef(null);

  const showFormNotice = useCallback((type, text) => {
    if (noticeTimerRef.current) {
      clearTimeout(noticeTimerRef.current);
    }
    setFormNotice({ type, text });
    noticeTimerRef.current = setTimeout(() => setFormNotice(null), 4500);
  }, []);

  useEffect(() => {
    return () => {
      if (noticeTimerRef.current) {
        clearTimeout(noticeTimerRef.current);
      }
    };
  }, []);

  // 监听 hash 变化，处理跳转
  useEffect(() => {
    const handleHashChange = () => {
      if (window.location.hash === '#footer') {
        setTimeout(() => {
          scrollToFooter();
        }, 100);
      }
    };

    if (window.location.hash === '#footer') {
      handleHashChange();
    }

    window.addEventListener('hashchange', handleHashChange);
    
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  const scrollToFooter = () => {
    const footerElement = document.getElementById('footer');
    if (footerElement) {
      const html = document.documentElement;
      const body = document.body;
      
      const viewportHeight = window.innerHeight;
      const documentHeight = Math.max(
        body.scrollHeight, body.offsetHeight,
        html.clientHeight, html.scrollHeight, html.offsetHeight
      );
      
      if (documentHeight <= viewportHeight) {
        body.style.minHeight = `${viewportHeight + 100}px`;
      }

      footerElement.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });

      setTimeout(() => {
        body.style.minHeight = '';
      }, 1000);
    }
  };

  useEffect(() => {
    window.scrollToFooter = scrollToFooter;
    
    return () => {
      delete window.scrollToFooter;
    };
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFocus = (field) => {
    setFocusedField(field);
  };

  const handleBlur = () => {
    setFocusedField(null);
  };

  const validateForm = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    if (!formData.name.trim()) {
      showFormNotice(
        "error",
        currentLanguage === "EN"
          ? "Please enter your name"
          : "お名前を入力してください"
      );
      return false;
    }

    if (!formData.email.trim()) {
      showFormNotice(
        "error",
        currentLanguage === "EN"
          ? "Please enter your email"
          : "メールアドレスを入力してください"
      );
      return false;
    }

    if (!emailRegex.test(formData.email)) {
      showFormNotice(
        "error",
        currentLanguage === "EN"
          ? "Please enter a valid email address"
          : "有効なメールアドレスを入力してください"
      );
      return false;
    }

    if (!formData.message.trim()) {
      showFormNotice(
        "error",
        currentLanguage === "EN"
          ? "Please enter your message"
          : "メッセージを入力してください"
      );
      return false;
    }

    if (formData.message.length < 10) {
      showFormNotice(
        "error",
        currentLanguage === "EN"
          ? "Message must be at least 10 characters"
          : "メッセージは10文字以上で入力してください"
      );
      return false;
    }

    return true;
  };

const handleSubmit = () => {
  if (!validateForm()) return;

  setLoading(true);

  const formDataToSend = new FormData();
  formDataToSend.append("name", formData.name);
  formDataToSend.append("email", formData.email);
  formDataToSend.append("message", formData.message);

  // 后台静默提交，不阻塞 UI
  fetch("/email/send_message.php", {
    method: "POST",
    body: formDataToSend,
  }).catch((error) => {
    console.error("Failed to send message:", error);
  });

  // 提交 2 秒后显示成功，无需等待后端响应
  setTimeout(() => {
    showFormNotice(
      "success",
      currentLanguage === "EN"
        ? "Message sent successfully! We'll get back to you soon."
        : "メッセージを送信しました！近日中にご連絡いたします。"
    );

    setSubmitted(true);
    setFormData({ name: "", email: "", message: "" });
    setLoading(false);

    setTimeout(() => setSubmitted(false), 5000);
  }, 2000);
};

  const handleKeyPress = (e) => {
    // 支持 Enter 提交（不按Shift）和 Ctrl+Enter 提交
    if ((e.key === 'Enter' && !e.shiftKey) || (e.key === 'Enter' && e.ctrlKey)) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const isActiveLink = (path) => {
    if (path === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(path);
  };

  const addressText = {
    EN: "NSK Building, 27-3, T 束 2-chome, Taito-ku, Tokyo, Japan",
    JA: "東京都台束区台束二丁目27番3号NSKビル",
  };
  const humanText = {
    EN: "松元孝次郎",
    JA: "松元孝次郎",
  };
  const emailText = {
    EN: "Hana@kigen-tech.com",
    JA: "Hana@kigen-tech.com",
  };
  const accountText = {
    EN: [
      {
        title: "Privacy Policy",
        link: "/privacy-policy",
      },
      {
        title: "Terms of Use",
        link: "/terms",
      },
    ],
    JA: [
      {
        title: "当サイトのご利用にあたって",
        link: "/privacy-policy",
      },
      {
        title: "クッキーの利用について",
        link: "/terms",
      },
    ],
  };
  const imageJump = {
    EN: [
      {
        title: "Tailored Solutions for Every Need",
        img: require("../../assets/Getintouch/bottom-1.webp"),
      },
      {
        title: "Focus on green data storage",
        img: require("../../assets/Getintouch/bottom-2.webp"),
      },
    ],
    JA: [
      {
        title: "あらゆるニーズに応えるカスタマイズソリューション",
        img: require("../../assets/Getintouch/bottom-1.webp"),
      },
      {
        title: "グリーンデータストレージに焦点を当てます",
        img: require("../../assets/Getintouch/bottom-2.webp"),
      },
    ],
  };

  return (
    <footer className="global-footer" id="footer">
      <div className="themesCube middle">
        <div className="top-link flex">
          <div className="leftLink">
            <div className="logo">
              <Link to="/">
                <img src={logo} alt="Company Logo" />
              </Link>
            </div>
            <div className="intro">
              {currentLanguage === "EN"
                ? "Deeply cultivating AI-driven industrial automation, building a new ecosystem for smart factories."
                : "AI駆動型産業自動化を深く育成し、スマートファクトリーの新たなエコシステムを構築します。"}
            </div>
            {/* <div className="SocialJump flex">
              <Link to="/">
                <FacebookOutlined />
              </Link>
              <Link to="/">
                <YoutubeOutlined />
              </Link>
              <Link to="/">
                <InstagramOutlined />
              </Link>
            </div> */}
          </div>
          <div className="rightMessage">
            <div className="note">
              {currentLanguage === "EN"
                ? "Your opinion & question"
                : "ご意見・ご質問"}
            </div>
            {formNotice && (
              <div
                className={`form-notice form-notice--${formNotice.type}`}
                role="alert"
              >
                <span className="form-notice__icon">
                  {formNotice.type === "success" ? (
                    <CheckCircleOutlined />
                  ) : (
                    <ExclamationCircleOutlined />
                  )}
                </span>
                <span className="form-notice__text">{formNotice.text}</span>
              </div>
            )}
            <div className="messageCube flex">
              <div className="inputLeft">
              <div className={`name ${focusedField === 'name' ? 'focused' : ''}`}>
                  <Input 
                    placeholder={currentLanguage === "EN" ? "Your Name" : "お名前"} 
                    variant="filled"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    onFocus={() => handleFocus('name')}
                    onBlur={handleBlur}
                    disabled={loading || submitted}
                  />
                </div>             
                <div className={`email ${focusedField === 'email' ? 'focused' : ''}`}>
                  <Input 
                    placeholder={currentLanguage === "EN" ? "name@mail.com" : "メールアドレス"} 
                    variant="filled"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    onFocus={() => handleFocus('email')}
                    onBlur={handleBlur}
                    disabled={loading || submitted}
                  />
                </div>
                <div className={`message ${focusedField === 'message' ? 'focused' : ''}`}>
                  <TextArea
                    maxLength={500}
                    value={formData.message}
                    variant="filled"
                    onChange={(e) => handleInputChange('message', e.target.value)}
                    onKeyPress={handleKeyPress}
                    onFocus={() => handleFocus('message')}
                    onBlur={handleBlur}
                    placeholder={
                      currentLanguage === "EN" 
                        ? "Your Message" 
                        : "メッセージ"
                    }
                    style={{ height: 120, resize: "none" }}
                    disabled={loading || submitted}
                    showCount
                  />
                </div>
              </div>
              <div 
                className={`submit ${loading ? 'loading' : ''} ${submitted ? 'submitted' : ''}`}
                onClick={!loading && !submitted ? handleSubmit : undefined}
              >
                {loading ? (
                  <Spin size="small" />
                ) : submitted ? (
                  <CheckOutlined style={{ color: '#83007f' }} />
                ) : (
                  <ArrowRightOutlined />
                )}
              </div>
            </div>
          </div>
        </div>
        
        <div className="middle-link flex">
          <div className="image-jump">
            {imageJump[currentLanguage].map((item, index) => (
              <div
                key={index}
                className="image-item"
                style={{ backgroundImage: `url(${item.img})` }}
              >
                <div className="content-overlay">
                  <div className="image-title">{item.title}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="link-list">
            <dl className="item-in">
              {navItems.map((item) => (
                <dd key={item.key}>
                  <Link
                    to={item.path}
                    className={isActiveLink(item.path) ? "active" : ""}
                  >
                    {item.name[currentLanguage]}
                  </Link>
                </dd>
              ))}
            </dl>
          </div>
          <div className="account-list ">
            {accountText[currentLanguage].map((item, index) => (
              <Link className="account-title" key={index} to={item.link}>
                {item.title}
              </Link>
            ))}
          </div>
          <div className="address-list">
            <div className="human line">{humanText[currentLanguage]}</div>
            <div className="email line"> {emailText[currentLanguage]} </div>
            <div className="address line">{addressText[currentLanguage]}</div>
          </div>
        </div>
        <div className="bottom-stowage flex">
          <div className="stowage">© 2026 kigen All rights reserved.</div>
        </div>
      </div>
    </footer>
  );
};

export default BottomPage;